// @typescript-eslint/no-floating-promises

import { toast } from "react-toastify"
import { H3 } from "~/ui-rtk/components/ui/typography"
import { ErrorMessage, Field, Form, Formik, FormikProps } from "formik"
import { Button } from "~/ui-rtk/components/ui/controls"
import { Divider } from "~/ui-rtk/components/ui/common"
import { useCompanyConnectorControllerGetCompanyConnectorsQuery } from "~/ui-rtk/api/companyConnectorApi"
import { useCompanyControllerUpdateCompanyMutation } from "~/ui-rtk/api/companyApi"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import MultiSelectWithChips, {
  TMultiSelectWithChipsOption,
} from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"

type syncStateFormValues = {
  syncStateValues: Array<{ value: string; label: string }>
}

export default function CompanySyncStateForm() {
  const { data: companyConnectors } =
    useCompanyConnectorControllerGetCompanyConnectorsQuery()
  const googleKeywordSearchConnector = companyConnectors?.find(
    connector => connector.serviceName === "google_keyword_search_v2",
  )

  const currentCompany = useCurrentCompany()

  const [updateCompany] = useCompanyControllerUpdateCompanyMutation()

  if (!googleKeywordSearchConnector) {
    return <div>No connector found</div>
  }

  return (
    <>
      <Divider className="my-3" />
      <H3>Google Keyword Search V2 Connector</H3>
      <Formik<syncStateFormValues>
        initialValues={{
          syncStateValues:
            currentCompany?.metadata?.keywords?.google?.regions?.map(
              (value: string) => ({
                value,
                label: value
                  .split("_")
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" "),
              }),
            ) ?? [],
        }}
        onSubmit={async values => {
          if (!googleKeywordSearchConnector?.id || !currentCompany?.id) return

          try {
            await updateCompany({
              id: currentCompany?.id,
              updateCompanyDto: {
                metadata: {
                  ...currentCompany?.metadata,
                  keywords: {
                    ...currentCompany?.metadata?.keywords,
                    google: {
                      searchAlias:
                        currentCompany?.metadata?.keywords?.google?.searchAlias,
                      brands:
                        currentCompany?.metadata?.keywords?.google?.brands ??
                        [],
                      competitors:
                        currentCompany?.metadata?.keywords?.google
                          ?.competitors ?? [],
                      categories:
                        currentCompany?.metadata?.keywords?.google
                          ?.categories ?? [],
                      hash:
                        currentCompany?.metadata?.keywords?.google?.hash ??
                        null,
                      regions: values.syncStateValues.map(
                        option => option.value,
                      ),
                    },
                  },
                },
              },
            }).unwrap()

            toast.success("State sync values updated successfully")
          } catch (error) {
            toast.error("Failed to update state sync values")
          }
        }}
      >
        {({ setFieldValue, values }: FormikProps<syncStateFormValues>) => (
          <Form>
            <div className="grid grid-cols-3 grid-rows-1 gap-8 py-3">
              <h3 className="row-span-2 py-3">State Sync Values:</h3>
              <div className="relative col-span-2">
                <div className="flex items-center gap-2">
                  <Field
                    id="syncStateValues"
                    name="syncStateValues"
                    as={MultiSelectWithChips}
                    groups={[
                      { label: "Oceania", value: "oceania" },
                      { label: "North America", value: "north_america" },
                      { label: "Asia", value: "asia" },
                      { label: "Europe", value: "europe" },
                      { label: "Region Sets", value: "region_sets" },
                    ]}
                    options={[
                      {
                        value: "Australia",
                        label: "Australia",
                        group: "oceania",
                      },
                      {
                        value: "New Zealand",
                        label: "New Zealand",
                        group: "oceania",
                      },
                      { value: "US", label: "US", group: "north_america" },
                      {
                        value: "Canada",
                        label: "Canada",
                        group: "north_america",
                      },
                      { value: "Singapore", label: "Singapore", group: "asia" },
                      { value: "Austria", label: "Austria", group: "europe" },
                      { value: "Belgium", label: "Belgium", group: "europe" },
                      { value: "Bulgaria", label: "Bulgaria", group: "europe" },
                      { value: "Croatia", label: "Croatia", group: "europe" },
                      { value: "Czechia", label: "Czechia", group: "europe" },
                      { value: "Denmark", label: "Denmark", group: "europe" },
                      { value: "Estonia", label: "Estonia", group: "europe" },
                      { value: "Finland", label: "Finland", group: "europe" },
                      { value: "France", label: "France", group: "europe" },
                      { value: "Germany", label: "Germany", group: "europe" },
                      { value: "Greece", label: "Greece", group: "europe" },
                      {
                        value: "Vatican City",
                        label: "Vatican City",
                        group: "europe",
                      },
                      { value: "Hungary", label: "Hungary", group: "europe" },
                      { value: "Iceland", label: "Iceland", group: "europe" },
                      { value: "Ireland", label: "Ireland", group: "europe" },
                      { value: "Italy", label: "Italy", group: "europe" },
                      {
                        value: "Liechtenstein",
                        label: "Liechtenstein",
                        group: "europe",
                      },
                      {
                        value: "Lithuania",
                        label: "Lithuania",
                        group: "europe",
                      },
                      {
                        value: "Luxembourg",
                        label: "Luxembourg",
                        group: "europe",
                      },
                      {
                        value: "Netherlands",
                        label: "Netherlands",
                        group: "europe",
                      },
                      { value: "Norway", label: "Norway", group: "europe" },
                      { value: "Poland", label: "Poland", group: "europe" },
                      { value: "Portugal", label: "Portugal", group: "europe" },
                      { value: "Romania", label: "Romania", group: "europe" },
                      { value: "Slovakia", label: "Slovakia", group: "europe" },
                      { value: "Slovenia", label: "Slovenia", group: "europe" },
                      { value: "Spain", label: "Spain", group: "europe" },
                      { value: "Sweden", label: "Sweden", group: "europe" },
                      {
                        value: "Switzerland",
                        label: "Switzerland",
                        group: "europe",
                      },
                      { value: "Turkey", label: "Turkey", group: "europe" },
                      {
                        value: "United Kingdom",
                        label: "United Kingdom",
                        group: "europe",
                      },
                      {
                        value: "Europe",
                        label: "Europe",
                        group: "region_sets",
                      },
                    ]}
                    placeholder="Select countries"
                    value={values.syncStateValues}
                    onChange={async (
                      selectedOptions: TMultiSelectWithChipsOption[],
                    ) => {
                      await setFieldValue("syncStateValues", selectedOptions)
                    }}
                  />
                  <ErrorMessage
                    component="a"
                    name="syncStateValues"
                    className="absolute input-error-message"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end pb-[20rem]">
              <Button
                variant={{ variant: "solid", color: "pink" }}
                size="sm"
                type="submit"
              >
                Update State Sync
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}
